// Customer page → Platforms & Profiles (mig 0017): the platforms this
// customer offers — each a named instance of a platform type (mig 0018),
// with its profile mode — and the customer profiles on each. Admin/senior
// can add platforms (several of one type under different names), rename
// them, change a mode while it has no profiles, deactivate a platform, and
// add profiles.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { selectClass } from '@/components/ui/field';
import { customerPlatforms, customerProfiles, lookups } from '@/lib/api';
import { PLATFORM_NAME_MAX, PROFILE_MODES } from '@frt/shared-types';
import type { CustomerPlatformRow, ProfileMode } from '@frt/shared-types';
import { ProfileFormDialog, type ProfilePreset } from '../profiles/profile-form';

export function PlatformsCard({ customerId, canEdit }: { customerId: number; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [platformId, setPlatformId] = useState(0);
  const [name, setName] = useState('');
  const [mode, setMode] = useState<ProfileMode>('per_tester');
  const [preset, setPreset] = useState<ProfilePreset | null>(null);

  const { data: platforms } = useQuery({
    queryKey: ['customer-platforms', customerId],
    queryFn: () => customerPlatforms.forCustomer(customerId),
  });
  const { data: profiles } = useQuery({
    queryKey: ['customer-profiles', { frt_customer_id: customerId }],
    queryFn: () => customerProfiles.list({ frt_customer_id: customerId, active: 'all' }),
  });
  const { data: platformTypes } = useQuery({
    queryKey: ['lookups', 'platform-options'],
    queryFn: () => lookups.platformOptions.list(),
  });

  const invalidate = () => {
    for (const key of ['customer-platforms', 'customer-profiles', 'customer-profile', 'accounts', 'account']) {
      void queryClient.invalidateQueries({ queryKey: [key] });
    }
  };
  const add = useMutation({
    mutationFn: () =>
      customerPlatforms.create(customerId, {
        platform_id: platformId,
        name: name.trim() || null,
        profile_mode: mode,
      }),
    onSuccess: () => {
      setPlatformId(0);
      setName('');
      invalidate();
    },
  });
  const update = useMutation({
    mutationFn: (args: { id: number; name?: string; profile_mode?: ProfileMode; active?: boolean }) =>
      customerPlatforms.update(args.id, args),
    onSuccess: invalidate,
  });

  const rows = platforms?.data ?? [];
  const types = (platformTypes?.data ?? []).filter((o) => o.active);
  const typeName = types.find((t) => t.id === platformId)?.name ?? '';

  return (
    <Card>
      <CardHeader>
        <CardTitle>Platforms &amp; online profiles</CardTitle>
      </CardHeader>
      <CardBody className="space-y-4">
        {rows.length === 0 && <p className="text-sm text-slate-500">No platforms yet.</p>}
        {rows.map((cp) => (
          <PlatformBlock
            key={cp.id}
            cp={cp}
            profiles={(profiles?.data ?? []).filter((p) => p.customer_platform_id === cp.id)}
            canEdit={canEdit}
            onRename={(n) => update.mutate({ id: cp.id, name: n })}
            onMode={(m) => update.mutate({ id: cp.id, profile_mode: m })}
            onActive={(a) => update.mutate({ id: cp.id, active: a })}
            onAddProfile={() => setPreset({ frt_customer_id: customerId, customer_platform_id: cp.id })}
          />
        ))}
        {update.error && <p className="text-sm text-red-600">{(update.error as Error).message}</p>}

        {canEdit && types.length > 0 && (
          <form
            className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3"
            onSubmit={(e) => {
              e.preventDefault();
              if (platformId) add.mutate();
            }}
          >
            <label className="block text-sm">
              <span className="block text-slate-600">Add platform — type</span>
              <select value={platformId} onChange={(e) => setPlatformId(Number(e.target.value))} className={selectClass}>
                <option value={0}>— select —</option>
                {types.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Name</span>
              <Input
                value={name}
                maxLength={PLATFORM_NAME_MAX}
                placeholder={typeName || 'e.g. Consumer Lending'}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
            <label className="block text-sm">
              <span className="block text-slate-600">Profiles</span>
              <ModeSelect value={mode} onChange={setMode} />
            </label>
            <Button type="submit" disabled={!platformId || add.isPending}>
              Add
            </Button>
            {add.error && <span className="text-sm text-red-600">{(add.error as Error).message}</span>}
            <p className="basis-full text-xs text-slate-500">
              Leave the name blank to use the type’s name. Add a type more than once under different
              names, e.g. “Consumer Deposits” and “Consumer Lending”.
            </p>
          </form>
        )}
      </CardBody>
      <ProfileFormDialog
        open={preset !== null}
        onClose={() => setPreset(null)}
        preset={preset ?? undefined}
        onSaved={() => undefined}
      />
    </Card>
  );
}

function PlatformBlock({
  cp,
  profiles,
  canEdit,
  onRename,
  onMode,
  onActive,
  onAddProfile,
}: {
  cp: CustomerPlatformRow;
  profiles: import('@frt/shared-types').CustomerProfileRow[];
  canEdit: boolean;
  onRename: (name: string) => void;
  onMode: (m: ProfileMode) => void;
  onActive: (a: boolean) => void;
  onAddProfile: () => void;
}) {
  const locked = cp.profile_count > 0;
  const [renaming, setRenaming] = useState<string | null>(null);
  return (
    <div className={`rounded border border-slate-200 p-3 space-y-2 ${cp.active ? '' : 'opacity-60'}`}>
      <div className="flex flex-wrap items-center gap-3">
        {renaming !== null ? (
          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (renaming.trim() && renaming.trim() !== cp.name) onRename(renaming.trim());
              setRenaming(null);
            }}
          >
            <Input
              autoFocus
              value={renaming}
              maxLength={PLATFORM_NAME_MAX}
              onChange={(e) => setRenaming(e.target.value)}
              className="w-56"
            />
            <Button type="submit" size="sm">
              Save
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setRenaming(null)}>
              Cancel
            </Button>
          </form>
        ) : (
          <span className="font-medium text-slate-800">
            {cp.name}
            {cp.name.toLowerCase() !== cp.platform_type_name.toLowerCase() && (
              <span className="ml-1 text-sm font-normal text-slate-500">({cp.platform_type_name})</span>
            )}
            {!cp.active && ' (inactive)'}
            {canEdit && (
              <button
                type="button"
                className="ml-2 text-xs font-normal text-brand-700 hover:underline"
                onClick={() => setRenaming(cp.name)}
              >
                Rename
              </button>
            )}
          </span>
        )}
        {canEdit ? (
          <span title={locked ? 'The mode can only change while the platform has no profiles' : undefined}>
            <ModeSelect value={cp.profile_mode} onChange={onMode} disabled={locked} compact />
          </span>
        ) : (
          <span className="text-sm text-slate-600">
            {PROFILE_MODES.find((m) => m.value === cp.profile_mode)?.label}
          </span>
        )}
        {canEdit && (
          <div className="ml-auto flex gap-2">
            {cp.active && (
              <Button type="button" size="sm" onClick={onAddProfile}>
                + Add profile
              </Button>
            )}
            <Button type="button" size="sm" variant="secondary" onClick={() => onActive(!cp.active)}>
              {cp.active ? 'Deactivate' : 'Reactivate'}
            </Button>
          </div>
        )}
      </div>
      {profiles.length === 0 ? (
        <p className="text-sm text-slate-500">No profiles on this platform.</p>
      ) : (
        <ul className="text-sm space-y-1">
          {profiles.map((p) => (
            <li key={p.id}>
              <Link to="/profiles/$id" params={{ id: String(p.id) }} className="text-brand-700 hover:underline">
                {p.display}
              </Link>
              <span className="text-slate-500">
                {' '}
                — {p.account_count} account{p.account_count === 1 ? '' : 's'}
                {!p.active && ' · inactive'}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ModeSelect({
  value,
  onChange,
  disabled,
  compact,
}: {
  value: ProfileMode;
  onChange: (m: ProfileMode) => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value as ProfileMode)}
      className={compact ? 'rounded border border-slate-300 bg-white px-2 py-1 text-sm' : selectClass}
    >
      {PROFILE_MODES.map((m) => (
        <option key={m.value} value={m.value} title={m.hint}>
          {m.label}
        </option>
      ))}
    </select>
  );
}
